/**
 * SEC-17 — application-layer payload encryption (docs/security.md §18.0.1,
 * extended per SEC-16 to cover every admin-portal↔backend request/response,
 * not just PHI-bearing ones — the same X25519 ECDH + AES-256-GCM protocol
 * the mobile app already ships (app/services/payloadCrypto.ts), ported to
 * the browser.
 *
 * X25519 keygen/ECDH uses @noble/curves (small, audited, pure JS) rather
 * than WebCrypto's native X25519 support, whose browser rollout history is
 * inconsistent enough not to bet an internal tool on without a compat
 * matrix nobody wants to maintain. AES-256-GCM and SHA-256 use the
 * browser's native `crypto.subtle`, universally supported.
 *
 * Mirrors services/appCheck.ts's structure (module-level `unavailable`
 * flag, fail-soft everywhere): if anything here fails, callers get a null
 * key back and requests go out unencrypted, exactly like today.
 */
import { x25519 } from '@noble/curves/ed25519.js';
import { env } from '../config/env';

const DEVICE_PRIVATE_KEY_STORAGE_KEY = 'mediclynk_admin_device_private_key';

let unavailable = false;
let devicePrivateKey: Uint8Array | null = null;
let devicePublicKeyB64: string | null = null;

// REST shared key, derived once against the backend's public key and reused
// for every subsequent encrypted request this session.
let restSharedKey: Uint8Array | null = null;

const toBase64 = (bytes: Uint8Array): string => btoa(String.fromCharCode(...bytes));
const fromBase64 = (b64: string): Uint8Array =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
const fromHex = (hex: string): Uint8Array =>
  Uint8Array.from(hex.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) ?? []);
// TypeScript's DOM lib types WebCrypto's BufferSource as requiring a concrete
// ArrayBuffer, but typed-array helpers (getRandomValues, subtle.digest, etc.)
// return Uint8Array<ArrayBufferLike>. Copy into a fresh ArrayBuffer-backed view.
const asBufferSource = (bytes: Uint8Array): Uint8Array<ArrayBuffer> => new Uint8Array(bytes);

/** True once keypair generation has failed — callers should treat encryption as unavailable. */
export function isUnavailable(): boolean {
  return unavailable;
}

/**
 * Generates the device's X25519 keypair on first call (checks localStorage
 * first — idempotent, safe to call on every app boot). No-ops if it's
 * already ready or generation previously failed.
 */
export function generateDeviceKeypair(): void {
  if (unavailable) return;
  if (devicePrivateKey && devicePublicKeyB64) return;

  try {
    const existing = localStorage.getItem(DEVICE_PRIVATE_KEY_STORAGE_KEY);
    if (existing) {
      devicePrivateKey = fromBase64(existing);
    } else {
      devicePrivateKey = x25519.utils.randomSecretKey();
      localStorage.setItem(DEVICE_PRIVATE_KEY_STORAGE_KEY, toBase64(devicePrivateKey));
    }
    devicePublicKeyB64 = toBase64(x25519.getPublicKey(devicePrivateKey));
  } catch (error) {
    unavailable = true;
    console.warn('[payloadCrypto] keypair generation failed — encryption disabled:', error);
  }
}

/** The device's base64 X25519 public key, or null if not ready / unavailable. */
export function getDevicePublicKey(): string | null {
  return devicePublicKeyB64;
}

/**
 * Derives a shared AES-256-GCM key from ECDH(deviceKeypair, otherPartyPublicKey),
 * matching the backend's KDF exactly (SHA-256 of the raw shared secret) — see
 * backend's session-cipher.util.ts and the cross-repo test vector asserted
 * identically in this file's test suite, the app's, and the backend's.
 * Returns null (never throws) if unavailable or the device keypair isn't ready.
 */
export async function deriveSharedKey(otherPartyPublicKeyB64: string): Promise<Uint8Array | null> {
  if (unavailable || !devicePrivateKey) return null;
  try {
    const sharedSecret = x25519.getSharedSecret(devicePrivateKey, fromBase64(otherPartyPublicKeyB64));
    const digest = await crypto.subtle.digest('SHA-256', sharedSecret);
    return new Uint8Array(digest);
  } catch (error) {
    console.warn('[payloadCrypto] ECDH derivation failed:', error);
    return null;
  }
}

let serverKeyFetch: Promise<{ publicKey: string; keyVersion: string; enabled: boolean } | null> | null =
  null;
let restServerKeyVersion: string | null = null;

export function getRestServerKeyVersion(): string | null {
  return restServerKeyVersion;
}

/**
 * Fetches the backend's public key from GET /crypto/server-key via a plain
 * `fetch()` — deliberately not through apiClient.ts's apiRequest(), since
 * apiRequest() itself needs to call ensureRestEncryptionReady() and a
 * circular call here would be its own bootstrap problem.
 */
async function fetchServerKey(): Promise<{
  publicKey: string;
  keyVersion: string;
  enabled: boolean;
} | null> {
  try {
    const response = await fetch(`${env.apiBaseUrl}/crypto/server-key`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.warn('[payloadCrypto] fetching server key failed:', error);
    return null;
  }
}

/**
 * Ensures REST encryption capability is ready: fetches + caches the
 * backend's public key (once per session) and derives the shared key from
 * it. Returns null (fail soft) if the device keypair, the server-key
 * fetch, or the remote `enabled` kill switch isn't ready/on.
 */
export async function ensureRestEncryptionReady(): Promise<Uint8Array | null> {
  if (restSharedKey) return restSharedKey;
  if (unavailable || !devicePrivateKey) return null;

  if (!serverKeyFetch) serverKeyFetch = fetchServerKey();
  const serverKey = await serverKeyFetch;
  if (!serverKey || !serverKey.enabled) return null;

  restServerKeyVersion = serverKey.keyVersion;
  const key = await deriveSharedKey(serverKey.publicKey);
  if (key) restSharedKey = key;
  return restSharedKey;
}

/** AES-256-GCM encrypt, matching the backend's iv:authTag:ciphertext hex wire format. */
export async function encryptWithKey(plaintext: string, key: Uint8Array): Promise<string | null> {
  try {
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      asBufferSource(key),
      'AES-GCM',
      false,
      ['encrypt'],
    );
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertextAndTag = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        asBufferSource(new TextEncoder().encode(plaintext)),
      ),
    );
    // WebCrypto appends the 16-byte auth tag to the ciphertext output, unlike
    // Node's cipher.getAuthTag() which exposes it separately — split it back
    // out to match the shared iv:authTag:ciphertext wire format.
    const authTag = ciphertextAndTag.slice(ciphertextAndTag.length - 16);
    const ciphertext = ciphertextAndTag.slice(0, ciphertextAndTag.length - 16);
    return `${toHex(iv)}:${toHex(authTag)}:${toHex(ciphertext)}`;
  } catch (error) {
    console.warn('[payloadCrypto] encrypt failed:', error);
    return null;
  }
}

/** AES-256-GCM decrypt of the iv:authTag:ciphertext wire format. Throws on malformed/tampered input. */
export async function decryptWithKey(payload: string, key: Uint8Array): Promise<string> {
  const parts = payload.split(':');
  if (parts.length !== 3) {
    throw new Error('Malformed encrypted payload — expected iv:authTag:ciphertext');
  }
  const [ivHex, authTagHex, ciphertextHex] = parts;
  const iv = fromHex(ivHex);
  const authTag = fromHex(authTagHex);
  const ciphertext = fromHex(ciphertextHex);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    asBufferSource(key),
    'AES-GCM',
    false,
    ['decrypt'],
  );
  const reassembled = new Uint8Array([...ciphertext, ...authTag]);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: asBufferSource(iv) },
    cryptoKey,
    asBufferSource(reassembled),
  );
  return new TextDecoder().decode(plaintext);
}

/** Test-only hook to reset module state between test files. */
export function __resetForTests(): void {
  unavailable = false;
  devicePrivateKey = null;
  devicePublicKeyB64 = null;
  restSharedKey = null;
  serverKeyFetch = null;
  restServerKeyVersion = null;
}
